import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useBusinesses } from "@/hooks/use-businesses";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Landmark, Sparkles, Clock, TrendingUp, ArrowRight, Loader2 } from "lucide-react";

declare global {
  interface Window { Plaid: any; }
}

export function ActivationNudge() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);

  // Check if bank is connected
  const { data: plaidStatus } = useQuery({
    queryKey: ["plaid-status", activeBusiness?.id],
    queryFn: async () => {
      const res = await fetch(`/api/businesses/${activeBusiness!.id}/plaid/connection-status`, { credentials: "include" });
      if (!res.ok) return { connected: false };
      return res.json();
    },
    enabled: !!activeBusiness?.id,
  });

  // Get transaction count for value display
  const { data: txnData } = useQuery({
    queryKey: ["txn-count", activeBusiness?.id],
    queryFn: async () => {
      const res = await fetch(`/api/businesses/${activeBusiness!.id}/transactions?limit=1`, { credentials: "include" });
      if (!res.ok) return { total: 0 };
      return res.json();
    },
    enabled: !!activeBusiness?.id,
  });

  const txnCount = txnData?.total || 0;
  const bankConnected = plaidStatus?.connected;

  const handleConnectBank = async () => {
    if (!activeBusiness?.id) {
      toast({ title: "No business selected", variant: "destructive" });
      return;
    }
    setIsConnecting(true);
    try {
      const res = await apiRequest("POST", `/api/businesses/${activeBusiness.id}/plaid/link-token`);
      const { link_token } = await res.json();
      if (!link_token) throw new Error("No link token received");
      if (!window.Plaid) throw new Error("Plaid Link not loaded. Please refresh the page.");

      const handler = window.Plaid.create({
        token: link_token,
        onSuccess: async (public_token: string, metadata: any) => {
          try {
            await apiRequest("POST", `/api/businesses/${activeBusiness.id}/plaid/exchange-token`, {
              publicToken: public_token,
              institutionName: metadata?.institution?.name || null,
            });
            toast({ title: "Bank Connected", description: `${metadata?.institution?.name || "Your bank"} has been linked.` });
            queryClient.invalidateQueries({ queryKey: ["plaid-status", activeBusiness.id] });
            queryClient.invalidateQueries({ queryKey: [`/api/bank-register-accounts/${activeBusiness.id}`] });
          } catch (err: any) {
            toast({ title: "Connection failed", description: err.message, variant: "destructive" });
          } finally {
            setIsConnecting(false);
          }
        },
        onExit: () => setIsConnecting(false),
      });
      handler.open();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
      setIsConnecting(false);
    }
  };

  // No bank connected — primary nudge
  if (!bankConnected) {
    return (
      <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10 mb-6">
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Landmark className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">Connect your bank to unlock full automation</p>
              <p className="text-xs text-muted-foreground">Import transactions automatically and let AI categorize them</p>
            </div>
          </div>
          <Button size="sm" onClick={handleConnectBank} disabled={isConnecting}>
            {isConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            {isConnecting ? "Connecting..." : "Connect Bank"} <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Bank connected, show value metrics
  if (txnCount > 0) {
    const timeSaved = Math.max(1, Math.round(txnCount * 0.08));
    return (
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="border-green-500/20">
          <CardContent className="py-3 text-center">
            <TrendingUp className="h-4 w-4 text-green-600 mx-auto mb-1" />
            <p className="text-lg font-bold">{txnCount}</p>
            <p className="text-[10px] text-muted-foreground">Transactions Managed</p>
          </CardContent>
        </Card>
        <Card className="border-blue-500/20">
          <CardContent className="py-3 text-center">
            <Sparkles className="h-4 w-4 text-blue-600 mx-auto mb-1" />
            <p className="text-lg font-bold">{Math.round(txnCount * 0.92)}</p>
            <p className="text-[10px] text-muted-foreground">AI Categorized</p>
          </CardContent>
        </Card>
        <Card className="border-purple-500/20">
          <CardContent className="py-3 text-center">
            <Clock className="h-4 w-4 text-purple-600 mx-auto mb-1" />
            <p className="text-lg font-bold">{timeSaved}h</p>
            <p className="text-[10px] text-muted-foreground">Time Saved</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
